/* eslint-disable angular/no-private-call */

import { autolistHelperModule } from '../helpers/autolist/autolist';
import { removeMatching } from '../utils/array-utils';
import {
  defineValue,
  flattenPrototypeChain,
  lazyProperty,
  pluck,
} from '../utils/object-utils/object-utils';
import { pascalCase, snakeCase } from '../utils/string-utils';
import { jsDataConfigModule } from '../config/js-data';
import { parseDates } from '../utils/date-utils';
import { jsDataModule } from '../vendor/js-data';
import { _createCommonStaticMethods } from './model-helper-common-static-methods';
import { _createSaveEmbedded } from './model-helper-save-embedded';

export const modelHelperModule = angular
  .module('Q.models.model-helper', [
    jsDataModule,
    jsDataConfigModule,
    autolistHelperModule,
  ])

  /**
   * This library is used to make more advanced models by including some extra methods, and also
   * wrapping default methods for js-data.
   *
   * The main improvements are:
   *
   * - Typed Resources
   *   A resource may define .meta.types, which will enable type-specific functionality for that
   *   model.
   *
   * - fetch and fetchAll
   *   These methods are added to the model, and will provide asynchronous access to instances whether they
   *   are already in the backing store or not.
   *
   * - loadMissingRelations
   *   Will only load relations that are missing on the instance.
   *
   */
  .factory(
    'ModelHelper',
    function (
      autolist,
      DS,
      $q,
      DSUtils,
      $rootScope,
      DSHttpAdapter,
      $timeout,
      $log,
      $injector,
    ) {
      //--------------------------------------------------------------------
      //
      //  COMMON STATIC METHODS: Methods added to the Model object itself
      //
      //--------------------------------------------------------------------

      let _commonStaticProperties = {
        first() {
          return this.getAll()[0];
        },
        last() {
          let a = this.getAll();
          return a[a.length - 1];
        },
      };

      // attach static properties to DS defaults for inheritance
      angular.forEach(_commonStaticProperties, (fn, key) => {
        Object.defineProperty(DS.defaults, key, { get: fn });
      });

      _createCommonStaticMethods(
        DS,
        DSUtils,
        DSHttpAdapter,
        $q,
        $injector,
        autolist,
      );

      //--------------------------------------------------------------------
      //
      // MODEL EVENTS
      //
      //--------------------------------------------------------------------

      function _addModelChangedEvents(config, Model) {
        Model.$broadcast = (type, attrs, action = type) => {
          const instance = DS.get(Model.name, attrs.id);

          let props = {
            id: attrs.id,
            config,
            instance,
            attrs,
            resourceName: Model.name,
            action,
          };

          $rootScope.$broadcast(
            `model.changed:${Model.name}:${attrs.id}`,
            props,
            type,
          );
          $rootScope.$broadcast(`model.changed:${Model.name}`, props, type);
          $rootScope.$broadcast('model.changed', props, type);
        };
        angular.forEach(
          { create: 'created', update: 'updated', destroy: 'destroyed' },
          (type, action) => {
            Model.on(`DS.after${pascalCase(action)}`, (resource, attrs) => {
              $timeout(() => {
                Model.$broadcast(type, attrs, action);
              }, 100);
            });
          },
        );
      }

      //--------------------------------------------------------------------
      //
      // ACTION ENDPOINTS
      //
      //--------------------------------------------------------------------

      /**
       * Enables new static methods that call to specific endpoints, with the ability to
       * automatically inject the resulting object.
       *
       * Configuration comes from config.meta.actionEndpoints, a hash of method names to options. If the options
       * value is true or an empty object, the defaults apply.  All options are passed to the HTTP adapter, as well,
       * allowing you to override endpoints and other settings in a general manner.
       *
       * The created methods have the form of
       *
       * .[methodName]( id|resource, options )
       *
       * Options is mixed on top of the action, allowing you to override some configuration on a per-call basis.
       *
       * @param config
       * @param config.segment: the end segment, defaults to the method name
       * @param config.endpoint: override the model's default endpoint
       * @param config.method: HTTP method, defaults to POST,
       * @param config.responseHandler: if provided, overrides the built-in response handler (which deserializes
       *              and attempts to inject the result if it has an ID).
       *              Set to angular.identity to get direct access to the HTTP response.
       * @param config.cacheResponse: prevent the default handler from attempting to inject the response.
       * @param config.serialize: unless falsey, serialize the data object (if present) using the model's serialize method
       */
      function _addActionEndpoints(config) {
        config.staticMethods = config.staticMethods || {};

        angular.forEach(config.meta.actionEndpoints, function (action, name) {
          if (!angular.isObject(action)) {
            if (action === true) action = {};
            if (angular.isFunction(action))
              action = { responseHandler: action };
          }

          action = angular.extend(
            {
              segment: snakeCase(name),
              method: 'POST',
              responseHandler: false,
              onSuccess: null,
              cacheResponse: true,
              serialize: true,
            },
            action,
          );

          config.staticMethods[name] = function (id, options) {
            const Model = this;

            if (!id) {
              let msg = `Called to actionEndpoint ${Model.name}.${name} without an id or instance: "${id}"`;
              $log.warn(msg);
              return $q.reject(msg);
            }

            let instance = id;
            id = DSUtils.resolveId(Model, id);
            if (!instance.id) {
              instance = Model.get(id) || { id: id };
            }

            options = angular.extend({}, action, options || {});

            options.url = Model.getEndpointUrl({
              id,
              segment: action.segment,
              options,
            });

            if (instance.$type) {
              // add in type param for typed models
              const what = options.method === 'GET' ? 'params' : 'data';
              options[what] = options[what] || {};
              options[what].$type = options[what].$type || instance.$type;
            }

            if (options.data && options.serialize) {
              // serialize the data model by default
              options.data = Model.serialize(Model.name, options.data || {});
            }

            function broadcast() {
              Model.$broadcast('update', instance);
              // currently only used by tracker service
              $rootScope.$broadcast('model.actionEndpoint.success', {
                options: options,
                model: Model,
                instance: instance,
                name: name,
              });
            }

            let responseHandler;
            // if a response handler was provided, wrap it so that we can pass in other useful information
            if (options.responseHandler) {
              responseHandler = function (res) {
                broadcast();
                return options.responseHandler(res, Model, instance, options);
              };
            } else {
              responseHandler = function (res) {
                broadcast();
                const data = options.deserialize
                  ? options.deserialize(Model.name, res)
                  : Model.deserialize(Model.name, res);
                if (data && data.id && options.cacheResponse) {
                  return Model.inject(data);
                } else if (data && data.id) {
                  return Model.createInstance(data);
                } else {
                  return data;
                }
              };
            }

            return DSHttpAdapter.HTTP(options)
              .then(responseHandler)
              .then((val) => {
                if (options.onSuccess) options.onSuccess(val);
                return val;
              });
          };

          config.staticMethods[name + 'WithAlert'] = function (...args) {
            let returnedPromise = this[name](...args);
            let appAlert =
              $injector.has('appAlert') && $injector.get('appAlert');
            if (appAlert) {
              returnedPromise = returnedPromise.catch(appAlert.modelError);
            }
            return returnedPromise;
          };
        });
      }

      //--------------------------------------------------------------------
      //
      // SERIALIZATION & DESERIALIZATION
      //
      //--------------------------------------------------------------------

      /**
       * Looks at all top-level Array properties. If the first child contains a numerical `position` property,
       * then we sort the array based on `position`
       * @param data
       */
      function sortOrderables(data) {
        if (Array.isArray(data)) {
          data.forEach(sortOrderables);
        } else {
          angular.forEach(data, (prop) => {
            if (
              Array.isArray(prop) &&
              prop[0] &&
              angular.isNumber(prop[0].position)
            ) {
              prop.sort((a, b) => a.position - b.position);
              // recurse
              prop.forEach(sortOrderables);
            } else if (angular.isObject(prop)) {
              // recurse
              sortOrderables(prop);
            }
          });
        }
      }

      /**
       * Extracts data and attempts to replace dates
       * @param resource
       * @param resp
       * @returns {*} parsed and extracted data
       */
      function deserialize(resource, resp) {
        const body = resp && resp.data ? resp.data : resp;
        if (!body) return body;
        if (angular.isString(resource)) {
          resource = DS.definitions[resource];
        }

        const result = parseDates(body.data || body);
        sortOrderables(result);

        if (Array.isArray(result) && resp.config.responseDataCacheKey) {
          const responseDataCacheKey = resp.config.responseDataCacheKey;
          resource.$responseData = resource.$responseData || {};
          resource.$responseData[responseDataCacheKey] = angular.copy(body);
          resource.$responseData[responseDataCacheKey].params = angular.copy(
            resp.config.params,
          );
        }

        const meta = body.meta;
        if (meta && !Array.isArray(result)) {
          if (resource.metaData) {
            angular.merge(resource.metaData, meta);
          } else {
            resource.metaData = meta;
          }
        }

        return result;
      }

      // handles the actual parsing of UI prefs
      function _parseUiPrefs(instance) {
        let uiPrefs = instance.uiPrefs;
        if (uiPrefs) {
          angular.forEach(uiPrefs, (v, k) => {
            try {
              uiPrefs[k] = JSON.parse(v);
            } catch (e) {
              delete uiPrefs[k];
            }
          });
        }
      }

      /**
       * Adds JSON parsing for uiPrefs field
       * @param resource
       * @param data
       * @returns {*}
       */
      function deserializeWithUiPrefs(resource, data) {
        const parsedData = deserialize(resource, data);
        if (angular.isArray(parsedData)) {
          parsedData.forEach(_parseUiPrefs);
        } else {
          _parseUiPrefs(parsedData);
        }
        return parsedData;
      }

      /**
       * Cleans an item of undesired properties before serialization
       * @param resource
       * @param data
       * @returns {*}
       */
      function cleanItemForSave(resource, data) {
        if (angular.isString(resource)) {
          resource = DS.definitions[resource];
        }
        let saved, notSaved, newData;
        data = angular.copy(data, {});
        if (resource.meta && resource.meta.saveIncludes) {
          saved = resource.meta.saveIncludes;
          newData = {};
          angular.forEach(data, function (val, id) {
            if (saved.indexOf(id) !== -1) {
              newData[id] = val;
            }
          });
          data = newData;
        } else if (resource.meta && resource.meta.saveExcludes) {
          notSaved = resource.meta.saveExcludes;
          newData = {};
          angular.forEach(data, function (val, id) {
            // do not include fields that are prefixed with _ or that are explicitly excluded
            if (id[0] !== '_' && notSaved.indexOf(id) === -1) {
              newData[id] = val;
            }
          });
          data = newData;
        }
        // recursively clean relationships
        angular.forEach(resource.relationList, function (rel) {
          let local = rel.localField;
          if (local && data[local]) {
            if (rel.type === 'hasMany') {
              let acc = [];
              angular.forEach(data[local], function (val, i) {
                acc[i] = cleanItemForSave(rel.relation, val);
              });
              data[local] = acc;
            } else {
              data[local] = cleanItemForSave(rel.relation, data[local]);
            }
          }
        });
        return data;
      }

      /**
       * Cleans item and encapsulates it. Angular or JSON.stringify handles dates for us.
       * @param resource
       * @param data
       * @returns {{data: *}} cleaned data
       */
      function serialize(resource, data) {
        return { data: cleanItemForSave(resource, data) };
      }

      /**
       * Adds JSON stringification for uiPrefs
       * @param resource
       * @param data
       * @returns {{data: *}}
       */
      function serializeWithUiPrefs(resource, data) {
        const out = serialize(resource, data);
        if (out.data.uiPrefs) {
          angular.forEach(
            out.data.uiPrefs,
            (v, k) => (out.data.uiPrefs[k] = JSON.stringify(v)),
          );
        }
        return out;
      }

      function afterInject(resource, data) {
        if (angular.isArray(data)) {
          data.forEach((d) => afterInject(resource, d));
        } else if (angular.isObject(data)) {
          angular.forEach(resource.relationList, function (rel) {
            const local = rel.localField;
            const $type = rel.$type || data.$type;
            const linked =
              typeof rel.link === 'boolean'
                ? rel.link
                : !!resource.linkRelations;
            if (!linked && local && $type === data.$type && data[local]) {
              // inject any references
              data[local] = DS.inject(rel.relation, data[local]);
            }
          });

          if (resource.meta.cleanInjected) {
            resource.meta.cleanInjected(data);
          }

          if (resource.meta.hasDigest && !data.digest) {
            // insert dummy digest if this model usually has one
            data.digest = {};
          }
        }
      }

      function _addDefaultValues(config) {
        const defaults = config.meta.defaults;
        ['afterInject', 'afterCreateInstance'].forEach((method) => {
          let oldMethod = config[method];
          config[method] = function setDefaultsAfter(resource, items) {
            if (!Array.isArray(items)) items = [items];
            items.forEach((item) => {
              angular.forEach(defaults, (val, k) => {
                if (item[k] === undefined) {
                  item[k] = angular.copy(val);
                }
              });
            });
            if (oldMethod) {
              oldMethod(resource, items);
            }
          };
        });
      }

      //--------------------------------------------------------------------
      //
      // DEFAULT CONFIGURATION: Default properties for model configs
      //
      //--------------------------------------------------------------------

      const _defaultConfig = {
        deserialize,

        serialize,

        afterInject,
      };

      //--------------------------------------------------------------------
      //
      // PRIMARY METHODS
      //
      //--------------------------------------------------------------------

      /**
       * Defines a resource using the ModelHelper improvements.
       * @param config
       * @returns {*}
       */
      function defineResource(config) {
        let Model;

        config = angular.extend(
          { meta: {}, methods: {} },
          _defaultConfig,
          config,
        );

        // Add a specialized method in for retrieving the value of enumerated properties
        config.methods.getEnum = function (name, property) {
          let key = pluck(property || name, this);
          if (key) {
            return Model.getEnum(name, key);
          } else {
            return '';
          }
        };

        if (config.cleanAfterDestroy) {
          let { parent, collection } = config.cleanAfterDestroy;
          config.afterEject = config.afterDestroy = cleanDestroyed(
            parent,
            collection,
          );
          delete config.cleanAfterDestroy;
        }

        if (config.meta.actionEndpoints) {
          _addActionEndpoints(config);
        }

        if (config.meta.defaults) {
          _addDefaultValues(config);
        }

        /**
         * Returns a cloned instance of this model.
         *
         * First, this flattens the prototype chain, to ensure we're getting a deep copy of all the properties.
         * This is specifically used for solution-review-dialog, which uses a prototype chain to make shallow
         * changes for playback. Then angular.copy is used to create a deep copy of the model.
         *
         * Finally, we use Model.createInstance to create a new instance of the flattened, cloned data.
         *
         * Just using `Model.createInstance(this)` would only create a shallow copy, and would also
         * lose properties set higher up the prototype chain.
         *
         * @returns {*} Deep, flattened clone of this model.
         */
        config.methods.$clone = function () {
          const dataFlattened = flattenPrototypeChain(this, Model);
          const dataCopy = angular.copy(dataFlattened);
          return Model.createInstance(dataCopy);
        };

        if (config.meta.types) {
          Model = _defineTypedResource(config);
        } else {
          Model = DS.defineResource(config);
        }

        Model.$dsModel = config.name;

        _addModelChangedEvents(config, Model);

        if (config.staticMethods) {
          angular.extend(Model, config.staticMethods);
        }

        // Monkey Patch to prevent errors when checking hasChanges on new models
        Model.hasChanges = (model, ...args) => {
          if (model && !model.id) return true;
          return DS.hasChanges(config.name, model, ...args);
        };

        $rootScope.$on('app.logout', function () {
          delete Model.meta.$$didFindMissing;
        });

        return Model;
      }

      /**
       * Fix for js-data items no longer being cleaned up after removal.
       * This looks for any parents that might be referencing the given
       * resource item, and updates their collection
       * @param parent
       * @param collection
       */
      function cleanDestroyed(parent, collection) {
        return function (resource, data) {
          if (angular.isArray(data)) {
            data.forEach(cleanDestroyed);
          } else {
            DS.definitions[parent].getAll().forEach((p) => {
              if (p[collection]) {
                let idx = p[collection].findIndex((ci) => ci.id === data.id);
                if (idx > -1) {
                  p[collection].splice(idx, 1);
                }
              }
            });
          }
          return $q.when(data);
        };
      }

      /**
       * Defines a typed resource, that includes type-specific functionality.
       *
       * @param config
       * @returns {*}
       */
      function _defineTypedResource(config) {
        // auto type items
        function asType(instance) {
          if (instance && instance.$type && instance.$asType) {
            instance.$asType();
          }
        }

        ['afterInject', 'afterCreateInstance'].forEach((method) => {
          let oldMethod = config[method];
          config[method] = function typedResourceAfter(options, instance) {
            if (Array.isArray(instance)) {
              instance.forEach(asType);
            } else {
              asType(instance);
            }
            if (oldMethod) {
              oldMethod(options, instance);
            }
          };
        });

        config.methods = config.methods || {};

        // provide the ability to "import" sub-typed methods into the instance
        config.methods.$asType = function () {
          if (!this.hasOwnProperty('$$typed') && this.$type) {
            defineValue(this, '$$typed', true);
            const self = this;
            angular.forEach(
              config.meta.types[this.$type],
              function (value, key) {
                if (key === 'afterTyped') return;
                if (key === 'computed') {
                  Object.defineProperties(self, value);
                } else {
                  defineValue(self, key, value);
                }
              },
            );
            if (config.meta.types[this.$type].afterTyped) {
              config.meta.types[this.$type].afterTyped(this);
            }
          }

          return this;
        };

        return DS.defineResource(config);
      }

      function _createDefineQuickList(autolist) {
        // special case to load in a list for quick lookups
        return function defineQuickList(Model, findAllProps, { sort } = {}) {
          lazyProperty(Model, 'quickList', () => {
            let loaded = false;
            let lookup = {};
            const updateList = (list) => {
              // mixin any existing items
              Model.getAll().forEach((instance) => {
                const idx = list.findIndex((v) => v.id === instance.id);
                if (idx > -1) {
                  list[idx] = instance;
                } else {
                  list.push(instance);
                }
              });
              lookup = {};
              list.forEach((item) => {
                lookup[item.id] = item;
              });
              return list;
            };
            const quickList = autolist.create((options) => {
              if (loaded) {
                const list = quickList.slice();
                // remove any destroyed items
                const event = options.event;
                if (event && event.type === 'destroyed' && event.data) {
                  removeMatching(list, (v) => v.id === event.data.id);
                }
                return updateList(list);
              } else {
                // asynchronously load the list
                return Model.retrieveAll(
                  angular.extend(
                    {
                      quickList: true,
                      orderBy: 'id ASC',
                      per: 1000,
                    },
                    findAllProps,
                  ),
                  { cacheResponse: false, bypassCache: true },
                ).then((list) => {
                  loaded = true;
                  return updateList(list);
                });
              }
            }, Model);
            if (sort) {
              quickList.autoSort(sort);
            }
            quickList.listen();
            quickList.get = (id) => lookup[id];
            return quickList;
          });
        };
      }

      return {
        defineResource,
        deserialize,
        deserializeWithUiPrefs,
        serialize,
        serializeWithUiPrefs,
        afterInject,
        saveEmbedded: _createSaveEmbedded($q),
        defineQuickList: _createDefineQuickList(autolist),
      };
    },
  );
